import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { CustomerDetail, CustomerListItem, Paginated } from "../types/dtos";

export type CustomerListParams = { page?: number; pageSize?: number; q?: string; status?: string };

async function fetchCustomers(params: CustomerListParams): Promise<Paginated<CustomerListItem>> {
  const res = await staffApi.get("/customers", { params });
  return res.data;
}

export function useCustomers(params: CustomerListParams): UseQueryResult<Paginated<CustomerListItem>> {
  return useQuery({
    queryKey: ["customers", params],
    queryFn: () => fetchCustomers(params),
    placeholderData: (prev: Paginated<CustomerListItem> | undefined) => prev,
  });
}

export function useCustomer(id: number | null): UseQueryResult<CustomerDetail> {
  return useQuery({
    queryKey: ["customers", id],
    queryFn: async () => {
      const res = await staffApi.get<CustomerDetail>(`/customers/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export type CustomerFormInput = {
  razaoSocial: string;
  nomeFantasia?: string;
  cnpjCpf: string;
  personType: "PF" | "PJ";
  email: string;
  phone?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
};

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CustomerFormInput) => staffApi.post("/customers", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<CustomerFormInput> }) =>
      staffApi.patch(`/customers/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useSetCustomerStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      staffApi.patch(`/customers/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.delete(`/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}

export function useInvitePortalAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.post(`/customers/${id}/invite-portal-access`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
}
